import useStoreSelector from "common/redux/useStoreSelector";
import * as NP from "number-precision";
import * as _ from "lodash";
import { OfferField, OfferFormState, ReducerDefinition } from "./useOfferBuilder";
import { OrderType } from "domain/orderType";
import Offer from "domain/offer";
import usePermission from "common/security/usePermission";
import Permissions from "common/security/permissions";
import { OfferTypeEnum } from "domain/offerType";
import usePositionSettings from "features/positions/usePositionSettings";
import offerTypeHelper from "domain/offerTypeHelper";
import { getCurrency, getDefaultHedgingSettings, getFuturesContract, getPositionSetting, getSecondLastBusinessDayOfLastMonth, isCashOfferWithOpenStorage } from "./helpers";
import useOfferings from "features/settings/offerings/useOfferings";
import useFcmAccounts from "features/fcmAccounts/useFcmAccounts";
import useHedgingSettings from "features/settings/hedging/useHedgingSettings";
import { listPaymentOptionsForOffer, PaymentOption } from "domain/paymentOption";
import useDeliveryMethods from "features/deliveryMethods/useDeliveryMethods";
import useDeliveryPeriods from "features/deliveryPeriods/useDeliveryPeriods";
import { useMemo } from "react";
import { firstBusinessDayOfNextYear, formatTodaysDate, getPreviousDayForGivenDate, toUtcDay } from "common/formatters";
import { TasFallbackOption } from "domain/tasFallbackOption";
import useLocations from "features/locations/useLocations";
import PhysicalLocation, { getPhysicalLocation } from "domain/physicalLocation";
import calculateNumberOfContracts from "./contractCalculator";
import { roundCents } from "common/decimal";
import useCustomFields from "features/merchants/useCustomFields";
import { listAllowedFieldsForOffer } from "features/merchants/customFieldHelpers";
import useFees from "features/merchants/useFees";
import { listAllowedFeesForOffer } from "features/merchants/feeHelpers";
import { evaluate } from "features/merchants/tokenEvaluator";
import { findConfiguredHedgeSetting, findHedgeSetting } from "features/settings/hedging/helpers";
import Merchant, { isDeferPayInterestEnabled, isFeatureEnabled, MerchantFeature } from "domain/merchant";
import DeliveryPeriod from "domain/deliveryPeriod";
import useActiveMerchant from "features/merchants/useActiveMerchant";
import { isDeliveryMethodValidForPeriod, resolveDefaultDeliveryMethod } from "./deliveryMethodHelper";
import useAllowedDeliveryMethods from "features/contracts/useAllowedDeliveryMethods";
import { FeeThin, FeeType, getFeeValue } from "domain/fee";
import useMappedLocations from "features/locations/mappings/useMappedLocations";
import useFeatureFlag from "features/featureFlags/useFeatureFlag";

type DeliveryPeriodsFilter = (deliveryPeriods: DeliveryPeriod[], offer: NullishPartial<Offer>, canPushBasis: boolean, merchant: Merchant) => DeliveryPeriod[]

export function useAddOfferReducers(roundCashPrice: boolean, defaultOffer: NullishPartial<Offer>, filterDeliveryPeriods: DeliveryPeriodsFilter, isPricingWithBasisOffer?: boolean): ReducerDefinition[] {
    return useOfferReducers(roundCashPrice, false, defaultOffer, filterDeliveryPeriods, isPricingWithBasisOffer);
}

export function useEditOfferReducers(roundCashPrice: boolean, filterDeliveryPeriods: DeliveryPeriodsFilter): ReducerDefinition[] {
    return useOfferReducers(roundCashPrice, true, null, filterDeliveryPeriods);
}

function useOfferReducers(roundCashPrice: boolean, isUpdate: boolean, defaultOffer: NullishPartial<Offer> | null, filterDeliveryPeriods: DeliveryPeriodsFilter, isPricingWithBasisOffer?: boolean): ReducerDefinition[] {
    const merchant = useActiveMerchant();
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);
    const userConfiguration = useStoreSelector(m => m.userState.userConfiguration);
    const myCommodities = useStoreSelector(m => m.myCommodities);
    const positionSettings = usePositionSettings();
    const offerings = useOfferings();
    const fcmAccounts = useFcmAccounts();
    const hedgingSettings = useHedgingSettings();
    const deliveryMethods = useDeliveryMethods();
    const deliveryPeriods = useDeliveryPeriods();
    const locations = useLocations();
    const customFields = useCustomFields();
    const fees = useFees();
    const allowedDeliveryMethods = useAllowedDeliveryMethods();
    const mappedLocations = useMappedLocations();

    const canPlaceHedges = usePermission(Permissions.PlaceLimitOrder);
    const canPushBasis = usePermission(Permissions.PushBasis);

    const isMerchantAutoOfferEnabled = merchant.enableAutoOffer;
    const isMerchantAutoPositionEnabled = merchant.enableAutoPosition;
    const showPushBasisFeature = useFeatureFlag("ShowPushBasis");

    const useHtaFeeFromDeliveryPeriod = !isFeatureEnabled(merchant, MerchantFeature.UseHtaFeeSchedule);

    const reducerDefinitions: ReducerDefinition[] = useMemo<ReducerDefinition[]>(() => {
        const reducers: ReducerDefinition[] = [
            {
                // clear out existing comment, so it doesn't get duplicated on submit
                fields: [], // run on initialization only
                runOnInitialization: true,
                reducer: (offer) => {
                    return { ...offer, comments: null }
                },
            },
            {
                // select matching delivery period on commodity change (or clear delivery period if no matching)
                fields: [OfferField.Commodity],
                reducer: (offer) => {
                    const deliveryPeriod = filterDeliveryPeriods(deliveryPeriods, offer, canPushBasis, merchant).find(dp => (
                        dp.commodity.id === offer.commodity?.id
                        && dp.start?.valueOf() === offer.deliveryPeriod?.start?.valueOf()
                        && dp.end?.valueOf() === offer.deliveryPeriod?.end?.valueOf()
                    )) ?? null;
                    return { ...offer, deliveryPeriod }
                }
            },
            {
                // set currency on commodity or type change
                fields: [OfferField.Commodity, OfferField.Type],
                reducer: (offer) => {
                    return {
                        ...offer,
                        currency: getCurrency(offer.type, myCommodities?.find(x => x.id === offer.commodity?.id)),
                    }
                }
            },
            {
                //Clear out delivery terms if not allowed
                fields: [OfferField.CustomerWill],
                reducer: (offer, form) => {
                    form.displayDeliveryTerms = displayDeliveryTerms(offer);
                    if (offer.deliveryTerms == null || form.displayDeliveryTerms) return offer;

                    return { ...offer, deliveryTerms: null };
                },
                runOnInitialization: true
            },
            {
                fields: [OfferField.DeliveryPeriod, OfferField.Commodity],
                reducer: offer => {
                    if (offer.deliveryPeriod?.defaultDeliveryMethod == null) return offer;

                    // Don't override the trader's chosen delivery method on Set Basis.
                    if (offer.type === OfferTypeEnum.FixBasisOnHta) return offer;

                    return { ...offer, deliveryMethod: offer.deliveryPeriod.defaultDeliveryMethod };
                }
            },
            {
                // clear delivery method if invalid for period
                fields: [OfferField.DeliveryPeriod],
                reducer: offer => {
                    if (isDeliveryMethodValidForPeriod(offer.deliveryMethod, offer.deliveryPeriod, merchant)) {
                        return offer;
                    } else {
                        return {
                            ...offer,
                            deliveryMethod: null,
                        }
                    }
                },
            },
            {
                // Set default delivery method when Type changes
                fields: [OfferField.Type],
                reducer: (offer, _form, oldOffer) => {
                    if (!offer.type) return offer;
                    if (_form.lockDeliveryMethod) return offer;

                    // Only recalculate if Type actually changed and delivery method wasn't also changed in the same update
                    if (offer.type === oldOffer?.type || offer.deliveryMethod !== oldOffer?.deliveryMethod) return offer;

                    const deliveryMethod = resolveDefaultDeliveryMethod(offer, merchant, allowedDeliveryMethods, offerings, isCustomer);

                    if (offer.deliveryMethod === deliveryMethod) {
                        return offer;
                    }

                    return {
                        ...offer,
                        deliveryMethod
                    };
                },
            },
            {
                //Set default delivery terms
                fields: [OfferField.DeliveryMethod, OfferField.Type, OfferField.CustomerWill, OfferField.Location, OfferField.DeliveryPeriod, OfferField.Commodity],
                reducer: (offer, form, prevOffer) => {
                    if (!form.displayDeliveryTerms) return offer;

                    const old = deliveryMethods.find(dmd => dmd.deliveryMethod === prevOffer.deliveryMethod);
                    const oldDeliveryTerms = evaluate(old?.defaultDeliveryTerms ?? "", offer);
                    const replaceDeliveryTerms = offer.deliveryTerms == null || offer.deliveryTerms === oldDeliveryTerms ||
                        (offer.location?.id !== prevOffer.location?.id && offer.deliveryTerms !== oldDeliveryTerms);
                    if (!replaceDeliveryTerms) {
                        return offer;
                    }

                    const newDm = deliveryMethods.find(dmd => dmd.deliveryMethod === offer.deliveryMethod);
                    // For some of the customers, delivery terms can be dynamic where it needs to evaluate the path
                    // to get the correct value. For example, FOB {{location.name}}.
                    const newDeliveryTerms = evaluate(newDm?.defaultDeliveryTerms ?? "", offer);
                    return {
                        ...offer,
                        deliveryTerms: newDeliveryTerms
                    };
                },
                runOnInitialization: true
            },
            {
                //Reset cash price and price lock
                fields: [OfferField.OrderType],
                reducer: (offer, form) => {
                    form.hasUserUpdatedCashPrice = false;

                    return {
                        ...offer,
                        isPriceLocked: false
                    };
                }
            },
            {
                //Clear Max Show on a type change, it starts blank for the new type
                //Icebergs don't support minis. Runs before the Place Hedge reducer so contracts get calculated without them
                fields: [OfferField.OrderType],
                reducer: offer => ({
                    ...offer,
                    maxShow: null,
                    useMini: offer.orderType === OrderType.Iceberg ? null : offer.useMini
                })
            },
            {
                //Clear out customer contract ID if not allowed
                fields: [OfferField.CustomerWill],
                reducer: (offer, form) => {
                    form.displayCustomerContractId = displayCustomerContractId(offer);
                    if (offer.customerContractId == null) return offer;

                    if ((offer.customerWillSell && merchant.generalSettings.displayCustomerContractIdForSell) ||
                        (!offer.customerWillSell && merchant.generalSettings.displayCustomerContractIdForBuy)) {
                        return offer;
                    }

                    return { ...offer, customerContractId: null };
                },
                excludeForUpdates: true,
                runOnInitialization: true
            },
            {
                //set buy/sell based on producer default value
                fields: [OfferField.CustomerAccount],
                reducer: offer => {
                    if (offer.producer == null) {
                        return offer;
                    }

                    const defaultWillSell = offer.producer.willSellDefaultValue
                    const customerWillSell = listCustomerWill(offer).includes(defaultWillSell)
                        ? defaultWillSell
                        : !defaultWillSell;
                    return {
                        ...offer,
                        customerWillSell: customerWillSell
                    };
                },
                excludeForUpdates: true
            },
            {
                //Push Basis
                fields: [OfferField.CustomerAccount, OfferField.CustomerWill, OfferField.Type, OfferField.OrderType],
                reducer: offer => {
                    const showPushBasis = shouldShowPushBasis(offer, canPushBasis);
                    return {
                        ...offer,
                        isBasisPushed: showPushBasis ? offer.isBasisPushed : false,
                        pushedBasis: showPushBasis ? offer.pushedBasis : null
                    }
                }
            },
            {
                //Determine if the Place Hedge Order toggle should be checked
                fields: [OfferField.CustomerWill, OfferField.OrderType, OfferField.Type, OfferField.DeliveryPeriod, OfferField.Commodity, OfferField.Location],
                reducer: (offer, form) => {
                    form.shouldShowHedgeOrderToggle = shouldShowPlaceHedgeToggle(offer);
                    form.placeHedgeOrder = shouldPlaceHedge(offer, form);

                    return form.placeHedgeOrder ? populateDefaultHedgingSettings(offer) : offer;
                },
                runOnInitialization: true
            },
            {
                //Mark as user having changed Place Hedge Order or clear out hedge info
                fields: [OfferField.PlaceHedge],
                reducer: (offer, form) => {
                    if (!form.placeHedgeOrder) {
                        //Clear out hedge settings
                        return { ...offer, fcmAccount: null, useMini: null, numberOfContracts: 0 }
                    }

                    return populateDefaultHedgingSettings(offer);
                },
                runOnInitialization: true
            },
            {
                //Clear invalid payment option
                fields: [OfferField.Type],
                reducer: offer => {
                    if (!merchant.generalSettings.displayPaymentOption) return offer;
                    const allowedPaymentOptions = listPaymentOptionsForOffer(offer.type);

                    if (!(allowedPaymentOptions as Nullish<PaymentOption>[]).includes(offer.paymentOption)) {
                        return { ...offer, paymentOption: allowedPaymentOptions[0], deferredPayDate: null }
                    }
                    return offer;
                },
                excludeForUpdates: true
            },
            {
                //Set isBasisFixed
                fields: [OfferField.Type],
                reducer: offer => {
                    const result = { ...offer }
                    if (offer.type === OfferTypeEnum.BasisOffer) {
                        // basis always fixed for basis offers
                        result.isBasisFixed = true;
                    }
                    if (offerTypeHelper.isHta(offer.type)) {
                        result.isBasisFixed = false;
                    }

                    return result;
                },
                excludeForUpdates: true
            },
            {
                //Set Delivery Period fields
                fields: [OfferField.DeliveryPeriod, OfferField.Commodity],
                reducer: offer => {
                    const dp = offer.deliveryPeriod;
                    // Don't auto-reset dates on Set Basis; they're independently editable.
                    const isSetBasis = offer.type === OfferTypeEnum.FixBasisOnHta;
                    // Keep the source contract's futures on pricing flows - it may be rolled
                    const isPricingExistingContract = isSetBasis || offer.type === OfferTypeEnum.FixFuturesOnBasis;
                    return {
                        ...offer,
                        futuresContract: isPricingExistingContract ? offer.futuresContract : getFuturesContract(offer.location?.id, dp),
                        deliveryPeriodStartDate: isSetBasis ? offer.deliveryPeriodStartDate : dp?.start,
                        deliveryPeriodEndDate: isSetBasis ? offer.deliveryPeriodEndDate : dp?.end
                    };
                }
            },
            {
                //Set expiration date
                fields: [OfferField.Type, OfferField.Commodity, OfferField.Symbol, OfferField.DeliveryMethod, OfferField.DeliveryPeriod, OfferField.OrderType],
                reducer: offer => {
                    let expirationDate: Nullish<Date>;
                    if ((offer.commodity ?? offer.deliveryPeriod?.commodity)?.isCashOnly) {
                        expirationDate = offer.deliveryPeriod?.end ?? offer.expirationDate;
                    }
                    else {
                        expirationDate = getExpirationDate(offer);
                    }

                    return {
                        ...offer,
                        expirationDate,
                        finalPricingDate: expirationDate
                    };
                },
                runOnInitialization: true,
                excludeForUpdateInitialization: true,
            },
            {
                //Set default fees
                fields: [OfferField.Type, OfferField.CustomerWill, OfferField.Commodity, OfferField.StartDate, OfferField.DeliveryPeriod],
                reducer: offer => {
                    if (offer.sourceContractId != null) {
                        return offer;
                    }

                    const offerForFees = isPricingWithBasisOffer ? { ...offer, type: OfferTypeEnum.BasisOffer } : offer;
                    const allowedFees = listAllowedFeesForOffer(fees, offerForFees);
                    const defaultFees = allowedFees
                        .filter(m => m.isDefault)
                        .map<FeeThin>(m => ({ ...m, value: getFeeValue(m, offer.deliveryPeriodStartDate), months: [] }));

                    const existingFees = offer.fees
                        ?.filter(m => !m.isDefault && allowedFees.some(f => f.id === m.id))
                        .map<FeeThin>(m => ({ ...m, value: getFeeValue(m, offer.deliveryPeriodStartDate), months: [] })) ?? [];

                    //TODO: remove this once everyone has been moved over to the custom fee schedule
                    if (useHtaFeeFromDeliveryPeriod && offerTypeHelper.isHta(offer.type) && offer.deliveryPeriod?.htaFee != null) {
                        const writingFee = defaultFees.find(m => m.type === FeeType.Writing);
                        if (writingFee != null) writingFee.value = offer.deliveryPeriod.htaFee;
                    }

                    return {
                        ...offer,
                        fees: _.uniqBy([...existingFees, ...defaultFees], m => m.id)
                    };
                },
                runOnInitialization: true,
                excludeForUpdates: true
            },
            {
                //Set default custom field values
                fields: [OfferField.Type, OfferField.CustomerWill, OfferField.Commodity, OfferField.Location, OfferField.InventoryLocation, OfferField.ContractLocation, OfferField.Trader],
                reducer: offer => {
                    return {
                        ...offer,
                        customFields: merchant.useCustomFields
                            ? listAllowedFieldsForOffer(customFields, offer)
                                .filter(cf => cf.defaultValue)
                                .map(cf => ({ id: cf.id, name: cf.name, value: cf.defaultValue }))
                            : []
                    };
                },
                runOnInitialization: true,
                excludeForUpdates: true
            },
            {
                //Set deferred pay fields
                fields: [OfferField.OrderType, OfferField.PaymentOption],
                reducer: offer => {
                    const isDeferredPay = offer.paymentOption === PaymentOption.DeferredPay;
                    const isLimit = offer.orderType === OrderType.Limit || offer.orderType === OrderType.Iceberg;
                    const shouldSetInterestStartDate = isDeferredPay && isDeferPayInterestEnabled(merchant);
                    return {
                        ...offer,
                        deferredPayDate: isDeferredPay
                            ? firstBusinessDayOfNextYear()
                            : null,
                        interestRate: isDeferredPay
                            ? merchant.generalSettings.defaultDeferPayInterestRate
                            : null,
                        interestStartDate: shouldSetInterestStartDate
                            ? (!isLimit ? formatTodaysDate('yyyy-MM-DD') : null)
                            : null,
                        setInterestStartDateOnDateOfFill: shouldSetInterestStartDate && isLimit
                            ? true
                            : null
                    }
                }
            },
            {
                //Set TAS Fallback Option
                fields: [OfferField.OrderType],
                reducer: offer => ({
                    ...offer,
                    tasFallbackOption: offer.orderType === OrderType.Tas
                        ? (offer.tasFallbackOption ?? TasFallbackOption.NotifyOnly)
                        : null
                })
            },
            {
                //Set region
                fields: [OfferField.Location],
                reducer: offer => ({
                    ...offer,
                    region: getRegion(offer.location)
                })
            },
            {
                // Clear out inventory/contract location (only inventory if pricing)
                // if there's only one option, autofill it
                // if inventory/contract is not displayed, autofill to default mapping
                fields: [OfferField.Location],
                reducer: offer => {
                    const isPricing = offer.type === OfferTypeEnum.FixFuturesOnBasis || offer.type === OfferTypeEnum.FixBasisOnHta;
                    const options = mappedLocations.getOptions(offer.location?.id);
                    return {
                        ...offer,
                        inventoryLocation: options.inventoryLocations.length === 1 ? options.inventoryLocations[0]
                            : merchant.generalSettings.displayInventoryLocation ? null
                                : mappedLocations.getDefault(offer.location?.id).inventoryLocation,
                        contractLocation: isPricing ? offer.contractLocation
                            : options.contractLocations.length === 1 ? options.contractLocations[0]
                                : merchant.generalSettings.displayContractLocation ? null
                                    : mappedLocations.getDefault(offer.location?.id).contractLocation,
                    };
                },
                runOnInitialization: false,
            },
            {
                // Clear out contract location (unless pricing)
                // if there's only 1 option, autofill it
                // if contract location is not displayed, autofill to default mapping
                fields: [OfferField.InventoryLocation],
                reducer: offer => {
                    const isPricing = offer.type === OfferTypeEnum.FixFuturesOnBasis || offer.type === OfferTypeEnum.FixBasisOnHta;
                    if (isPricing) {
                        return offer;
                    } else {
                        const options = mappedLocations.getOptions(offer.location?.id, offer.inventoryLocation?.id);
                        return {
                            ...offer,
                            contractLocation: options.contractLocations.length === 1 ? options.contractLocations[0]
                                : merchant.generalSettings.displayContractLocation ? null
                                    : mappedLocations.getDefault(offer.location?.id, offer.inventoryLocation?.id).contractLocation
                        };
                    }
                },
                runOnInitialization: false
            },
            {
                //Set default buy location
                fields: [OfferField.CustomerWill],
                reducer: offer => {
                    const defaultBuyLocation = locations.find(l => l.isDefaultBuyLocation);
                    if (offer.customerWillSell || defaultBuyLocation == null) return offer;

                    return {
                        ...offer,
                        location: defaultBuyLocation,
                        contractLocation: defaultBuyLocation,
                        region: getRegion(defaultBuyLocation)
                    }
                },
                excludeForUpdates: true
            },
            {
                //Clear order type
                fields: [OfferField.Type],
                reducer: offer => ({
                    ...offer,
                    orderType: offerTypeHelper.canBeHedged(offer.type)
                        ? offer.orderType ?? defaultOffer?.orderType
                        : null
                }),
                runOnInitialization: true,
                excludeForUpdates: true
            },
            {
                //Set number of contracts
                fields: [OfferField.Quantity, OfferField.Commodity, OfferField.PlaceHedge],
                reducer: (offer, form) => form.placeHedgeOrder
                    ? {
                        ...offer,
                        numberOfContracts: calculateNumberOfContracts(
                            (offer.commodity ?? offer.deliveryPeriod?.commodity),
                            offer.quantity,
                            Boolean(offer.useMini),
                            findHedgeSetting(hedgingSettings, offer),
                            getPositionSetting(positionSettings, offer))
                    }
                    : offer
            },
            {
                //Set basis
                fields: [OfferField.DeliveryPeriod, OfferField.Location, OfferField.Type],
                reducer: offer => ({
                    ...offer,
                    basis: getBasis(offer)
                })
            },
            {
                //Push basis
                fields: [OfferField.PushBasis],
                reducer: offer => ({
                    ...offer,
                    pushedBasis: null,
                    isBasisFixed: false
                })
            },
            {
                //Set futures price
                fields: [OfferField.DeliveryPeriod, OfferField.Type],
                reducer: offer => ({
                    ...offer,
                    futuresPrice: getFuturesPrice(offer, isUpdate)
                })
            },
            {
                //Set futures contract
                fields: [OfferField.Location],
                reducer: offer => {
                    // Keep the source contract's futures on pricing flows - it may be rolled
                    const isPricingExistingContract = offer.type === OfferTypeEnum.FixBasisOnHta || offer.type === OfferTypeEnum.FixFuturesOnBasis;
                    return {
                        ...offer,
                        futuresContract: isPricingExistingContract ? offer.futuresContract : getFuturesContract(offer.location?.id, offer.deliveryPeriod),
                    };
                }
            },
            {
                //Calculate cash price
                fields: [OfferField.Type, OfferField.CustomerWill, OfferField.CustomerAccount, OfferField.DeliveryPeriod, OfferField.Symbol, OfferField.Basis, OfferField.PushBasis, OfferField.PushBasisValue, OfferField.OrderType, OfferField.Location],
                reducer: (offer, form, prevOffer) => ({
                    ...offer,
                    cashPrice: getRoundedCashPrice(calculateCashPrice(offer, form, prevOffer), offer, form)
                }),
                runOnInitialization: true
            },
            {
                fields: [OfferField.FuturesPrice],
                reducer: offer => {
                    if (offer.type !== OfferTypeEnum.FixFuturesOnBasis &&
                        offer.orderType !== OrderType.ManualEntry) return offer;

                    offer.cashPrice = NP.plus(offer.futuresPrice ?? 0, offer.basis ?? 0, offer.pushedBasis ?? 0);
                    return offer;
                }
            },
            {
                //Cash price rounding
                fields: [OfferField.CashPrice],
                reducer: (offer, form) => ({
                    ...offer,
                    cashPrice: getRoundedCashPrice(offer.cashPrice, offer, form)
                })
            },
            {
                //Clear out cash for Basis
                fields: [OfferField.Type],
                reducer: offer => ({
                    ...offer,
                    cashPrice: offerTypeHelper.isBasis(offer.type)
                        ? null
                        : offer.cashPrice
                }),
                excludeForUpdates: true
            },
            {
                //Clear out fields for Delayed Price
                fields: [OfferField.Type],
                reducer: offer => {
                    if (offer.type !== OfferTypeEnum.DelayedPriceContract) {
                        return offer;
                    }

                    return {
                        ...offer,
                        cashPrice: null,
                        basis: null,
                        pushedBasis: null,
                        futuresContract: null,
                        deliveryPeriod: null
                    };
                },
                excludeForUpdates: true
            },
            {
                //Clear out fields for BushelOnlyContract
                fields: [OfferField.Type],
                reducer: offer => {
                    if (offer.type !== OfferTypeEnum.BushelOnlyContract) {
                        return offer;
                    }
                    return {
                        ...offer,
                        futuresPrice: offer.bushelOnlyContract?.futuresPrice ?? null,
                        cashPrice: null,
                        basis: offer.bushelOnlyContract?.basis ?? null,
                        pushedBasis: null
                    };
                },
                excludeForUpdates: true
            },
            {
                //Clear out price lock or price at open for basis offers
                fields: [OfferField.Type],
                reducer: offer => {
                    if (offer.type !== OfferTypeEnum.BasisOffer) {
                        return offer;
                    }

                    return {
                        ...offer,
                        isPriceAtOpenEnabled: false,
                        isPriceLocked: null
                    };
                },
                excludeForUpdates: true
            },
            {
                //Set TAS price to 0 when unhedged
                fields: [OfferField.PlaceHedge],
                reducer: (offer, form) => {
                    if (offer.orderType !== OrderType.Tas || form.placeHedgeOrder) return offer;

                    return {
                        ...offer,
                        cashPrice: 0,
                    };
                }
            }
        ];

        return reducers.filter(m => (!isUpdate || m.excludeForUpdates !== true));
    }, [customFields]);

    function shouldShowPushBasis(offer: NullishPartial<Offer>, canPushBasis: boolean) {
        if (!showPushBasisFeature)
            return false;
        if (isCustomer || !canPushBasis || offer.orderType === OrderType.ManualEntry)
            return false;
        if (offerTypeHelper.hasBasis(offer.type)) {
            return offer.customerWillSell;
        }
        return false;
    }

    function calculateCashPrice(offer: NullishPartial<Offer>, form: OfferFormState, prevOffer: NullishPartial<Offer>): Nullish<number> {
        if (offerTypeHelper.isBasis(offer.type)) {
            return null;
        }

        if (form.hasUserUpdatedCashPrice || (offer.isPriceLocked && offer.type === prevOffer.type)) {
            return offer.cashPrice;
        }

        if ((offer.commodity ?? offer.deliveryPeriod?.commodity)?.isCashOnly) {
            const deliveryPeriod = deliveryPeriods?.find(dp => dp.id === offer.deliveryPeriod?.id) ?? offer.deliveryPeriod;
            return deliveryPeriod?.bids?.find(x => x.location?.id === offer.location?.id)?.cash;
        }

        if (offer.orderType === OrderType.Tas) {
            return 0;
        }

        return NP.plus(offer.futuresPrice ?? 0, offer.basis ?? 0, offer.pushedBasis ?? 0);
    }

    function shouldRoundCashPrice(offer: NullishPartial<Offer>) {
        return offerTypeHelper.isCash(offer.type) && offer.deliveryPeriod != null
            && !([OrderType.Tas, OrderType.ManualEntry] as Nullish<OrderType>[]).includes(offer.orderType);
    }

    function getRoundedCashPrice(cashPrice: Nullish<number>, offer: NullishPartial<Offer>, form: OfferFormState): Nullish<number> {
        if (!cashPrice || !roundCashPrice || !shouldRoundCashPrice(offer) || form.hasUserUpdatedCashPrice) {
            return cashPrice;
        }

        return roundCents(cashPrice, offer.futuresContract?.isSettled
            ? offer.deliveryPeriod?.commodity.roundingOnClose
            : offer.deliveryPeriod?.commodity.roundingInSession);
    }

    function shouldShowPlaceHedgeToggle(offer: NullishPartial<Offer>): boolean {
        if (!canPlaceHedges ||
            offer.orderType === OrderType.ManualEntry ||
            offer.orderType === OrderType.Iceberg || // hedging is implied for Iceberg, toggle stays hidden

            offer.type === OfferTypeEnum.BushelOnlyContract ||
            !offerTypeHelper.canBeHedged(offer.type) ||
            (offer.commodity ?? offer.deliveryPeriod?.commodity)?.isCashOnly) {
            return false;
        }

        const positionSetting = getPositionSetting(positionSettings, offer);

        // Set Futures and Market Order and AO is off and AP is on and side is same as Auto order side
        if (offer.type === OfferTypeEnum.FixFuturesOnBasis && !isMerchantAutoOfferEnabled && isMerchantAutoPositionEnabled &&
            offer.orderType === OrderType.Market &&
            offer.customerWillSell === positionSetting?.autoOrderWillSell)
            return false;


        if (offer.order?.isManual)
            return true;

        // AO is off but AP is on
        if (!isMerchantAutoOfferEnabled && isMerchantAutoPositionEnabled)
            return true;

        // HTA Offer - No Location or (AO and AP are both off)
        if (offerTypeHelper.isHta(offer.type) && (offer.location == null || (!isMerchantAutoOfferEnabled && !isMerchantAutoPositionEnabled))) {
            if (isMerchantAutoOfferEnabled && offer.location == null && findConfiguredHedgeSetting(hedgingSettings, offer) != null)
                return false;

            return true;
        }

        if (!isMerchantAutoOfferEnabled &&
            (offerTypeHelper.isCash(offer.type) || offer.type === OfferTypeEnum.FixFuturesOnBasis)) {
            return true;
        }

        // AO is on and side is opposite of Auto order side
        // Do check for positionSetting null when not comparing for equality
        if (positionSetting != null && isMerchantAutoPositionEnabled && offer.customerWillSell !== positionSetting.autoOrderWillSell)
            return true;

        if (offer.orderType === OrderType.Tas)
            return true;

        return false;
    }

    function shouldPlaceHedge(offer: NullishPartial<Offer>, form: OfferFormState): boolean {
        if (!offerTypeHelper.canBeHedged(offer.type)) {
            return false;
        }

        if (offer.orderType === OrderType.ManualEntry) {
            return true;
        }

        // Iceberg offers always place a hedge upon submission
        if (offer.orderType === OrderType.Iceberg) {
            return true;
        }

        if (isMerchantAutoOfferEnabled &&
            offerTypeHelper.isHta(offer.type) &&
            offer.location == null &&
            findConfiguredHedgeSetting(hedgingSettings, offer) != null) {
            return true;
        }

        if (!form.shouldShowHedgeOrderToggle) {
            return false;
        }

        if (offer.order != null) {
            return true;
        }

        if (!isMerchantAutoOfferEnabled &&
            offerTypeHelper.isCash(offer.type) &&
            offer.orderType !== OrderType.Tas) {
            return false;
        }

        return true;
    }

    function getExpirationDate(offer: NullishPartial<Offer>): Nullish<Date> {
        if (isCashOfferWithOpenStorage(offer) || offer.type === OfferTypeEnum.DelayedPriceContract) {
            return offer.deliveryPeriod?.end;
        }
        else if(offerTypeHelper.isCash(offer.type) && (offer.orderType === OrderType.Market || offer.orderType === OrderType.Limit)){
            return  _.min([toUtcDay(offer.deliveryPeriod?.end), getPreviousDayForGivenDate(toUtcDay(offer.futuresContract?.expiration))]);
        }
        else if (offer.orderType === OrderType.Market || offer.orderType === OrderType.ManualEntry) {
            return getPreviousDayForGivenDate(toUtcDay(offer.futuresContract?.expiration));
        }
       
        return getSecondLastBusinessDayOfLastMonth(offer.futuresContract?.expiration);
    }

    function listCustomerWill(forOffer: NullishPartial<Offer>) {
        if (forOffer == null) return [];

        if (!isCustomer) {
            if (userConfiguration.canBuy && userConfiguration.canSell)
                return [true, false];
            else
                return [!userConfiguration.canBuy];
        }

        const offering = offerings
            .find(o => o.location?.id === forOffer.location?.id && o.commodity.id === forOffer.commodity?.id);

        const customerWillList: boolean[] = [];

        if (offering?.allowSelling) customerWillList.push(offering.allowSelling);
        if (offering?.allowBuying) customerWillList.push(!offering.allowBuying);

        return customerWillList;
    }

    function populateDefaultHedgingSettings(offer: NullishPartial<Offer>): NullishPartial<Offer> {

        const result = getDefaultHedgingSettings(merchant, offer, positionSettings, hedgingSettings, fcmAccounts);

        return { ...offer, fcmAccount: result.fcmAccount, numberOfContracts: result.numberOfContracts, maxShow: result.maxShow ?? offer.maxShow };
    }

    function getRegion(l: Nullish<PhysicalLocation>) {
        if (merchant.generalSettings.displayRegion) {
            if (l == null) {
                return locations.find(m => m.homeLocation == null);
            }
            else {
                return l.homeLocation != null ? getPhysicalLocation(l.homeLocation) : l;
            }
        }
        return null;
    }

    function getBasis(offer: NullishPartial<Offer>): Nullish<number> {
        if (offer.isBasisFixed && offer.basis != null && offer.type !== OfferTypeEnum.BasisOffer && !isPricingWithBasisOffer)
            return offer.basis;

        if (offer.bushelOnlyContract?.basis != null && (offer.type === OfferTypeEnum.BasisContract || offerTypeHelper.isCash(offer.type) || offer.type === OfferTypeEnum.BushelOnlyContract))
            return offer.bushelOnlyContract.basis;

        if (offer.deliveryPeriod?.commodity?.isCashOnly) {
            return null;
        }
        if (offer.type === OfferTypeEnum.FixFuturesOnBasis) {
            return offer.basis;
        }
        else if (offer.location == null || offerTypeHelper.isHta(offer.type)) {
            return null;
        }

        const deliveryPeriod = deliveryPeriods?.find(dp => dp.id === offer.deliveryPeriod?.id) ?? offer.deliveryPeriod;
        const bid = deliveryPeriod?.bids?.find(x => x.location?.id === offer.location?.id);
        return bid?.basis;
    }

    function getFuturesPrice(offer: NullishPartial<Offer>, isUpdate: boolean): Nullish<number> {
        if (offer.bushelOnlyContract?.futuresPrice != null && offerTypeHelper.isCash(offer.type))
            return offer.bushelOnlyContract.futuresPrice;

        if (!isUpdate)
            return offer.futuresPrice;

        if (!([OfferTypeEnum.Cash, OfferTypeEnum.CashMinPrice] as Nullish<OfferTypeEnum>[]).includes(offer.type))
            return offer.futuresPrice;

        const basis = getBasis(offer);
        return NP.minus(offer.cashPrice ?? 0, NP.plus(basis ?? 0, offer.pushedBasis ?? 0));
    }

    function displayDeliveryTerms(offer: NullishPartial<Offer>): boolean {
        if (isCustomer || ([OfferTypeEnum.FixBasisOnHta, OfferTypeEnum.FixFuturesOnBasis, OfferTypeEnum.DelayedPriceContract] as Nullish<OfferTypeEnum>[]).includes(offer.type))
            return false;
        if (!merchant.generalSettings.displayDeliveryTermsForBuy && !offer.customerWillSell)
            return false;
        if (!merchant.generalSettings.displayDeliveryTermsForSell && offer.customerWillSell)
            return false;
        return true;
    }

    function displayCustomerContractId(offer: NullishPartial<Offer>): boolean {
        if (isCustomer)
            return false;
        if (!merchant.generalSettings.displayCustomerContractIdForBuy && !offer.customerWillSell)
            return false;
        if (!merchant.generalSettings.displayCustomerContractIdForSell && offer.customerWillSell)
            return false;
        return true;
    }

    return reducerDefinitions;
}
